import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft } from '@phosphor-icons/react';

export default function Cookies() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="max-w-4xl mx-auto px-6 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary mb-8">
          <ArrowLeft size={20} />
          Retour à l'accueil
        </Link>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-4xl font-bold mb-8">Politique de Cookies</h1>
          <p className="text-muted-foreground mb-8">Dernière mise à jour : Décembre 2025</p>
          
          <div className="prose prose-invert max-w-none space-y-8">
            <section>
              <h2 className="text-2xl font-semibold mb-4">1. Qu'est-ce qu'un cookie ?</h2>
              <p className="text-muted-foreground leading-relaxed">
                Un cookie est un petit fichier texte stocké sur votre appareil (ordinateur, smartphone, tablette) 
                lorsque vous visitez un site web. Les cookies permettent au site de mémoriser vos actions et 
                préférences pendant une période déterminée.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">2. Types de cookies utilisés</h2>
              
              <div className="mt-6 p-4 rounded-lg bg-white/5 border border-white/10">
                <h3 className="text-lg font-semibold text-green-400 mb-2">Cookies essentiels (obligatoires)</h3>
                <p className="text-muted-foreground text-sm mb-2">
                  Ces cookies sont nécessaires au fonctionnement du site et ne peuvent pas être désactivés.
                </p>
                <ul className="list-disc list-inside text-muted-foreground text-sm space-y-1">
                  <li><strong>session_token</strong> : Maintient votre connexion (7 jours)</li>
                  <li><strong>csrf_token</strong> : Protection contre les attaques CSRF</li>
                </ul>
              </div>
              
              <div className="mt-4 p-4 rounded-lg bg-white/5 border border-white/10">
                <h3 className="text-lg font-semibold text-blue-400 mb-2">Cookies de fonctionnalité</h3>
                <p className="text-muted-foreground text-sm mb-2">
                  Ces cookies permettent d'améliorer votre expérience utilisateur.
                </p>
                <ul className="list-disc list-inside text-muted-foreground text-sm space-y-1">
                  <li><strong>user_preferences</strong> : Mémorise vos préférences (thème, langue)</li>
                  <li><strong>cookie_consent</strong> : Enregistre votre choix de cookies</li>
                </ul>
              </div>
              
              <div className="mt-4 p-4 rounded-lg bg-white/5 border border-white/10">
                <h3 className="text-lg font-semibold text-yellow-400 mb-2">Cookies analytiques (optionnels)</h3>
                <p className="text-muted-foreground text-sm mb-2">
                  Ces cookies nous aident à comprendre comment vous utilisez le site.
                </p>
                <ul className="list-disc list-inside text-muted-foreground text-sm space-y-1">
                  <li><strong>_ga, _gid</strong> : Google Analytics (si activé)</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">3. Durée de conservation</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-muted-foreground">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-3 px-4">Cookie</th>
                      <th className="text-left py-3 px-4">Durée</th>
                      <th className="text-left py-3 px-4">Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-white/5">
                      <td className="py-3 px-4">session_token</td>
                      <td className="py-3 px-4">7 jours</td>
                      <td className="py-3 px-4">Essentiel</td>
                    </tr>
                    <tr className="border-b border-white/5">
                      <td className="py-3 px-4">cookie_consent</td>
                      <td className="py-3 px-4">1 an</td>
                      <td className="py-3 px-4">Fonctionnel</td>
                    </tr>
                    <tr className="border-b border-white/5">
                      <td className="py-3 px-4">user_preferences</td>
                      <td className="py-3 px-4">1 an</td>
                      <td className="py-3 px-4">Fonctionnel</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4">_ga</td>
                      <td className="py-3 px-4">2 ans</td>
                      <td className="py-3 px-4">Analytique</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">4. Gestion des cookies</h2>
              <p className="text-muted-foreground leading-relaxed">
                Lors de votre première visite, une bannière vous permet de choisir quels cookies vous acceptez. 
                Vous pouvez modifier vos préférences à tout moment :
              </p>
              <ul className="list-disc list-inside text-muted-foreground mt-4 space-y-2">
                <li>Via le bouton "Gérer les cookies" en bas de chaque page</li>
                <li>Via les paramètres de votre navigateur</li>
              </ul>
              <p className="text-muted-foreground mt-4">
                <strong>Note :</strong> Le refus des cookies essentiels peut empêcher le fonctionnement de certaines 
                fonctionnalités du site.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">5. Cookies tiers</h2>
              <p className="text-muted-foreground leading-relaxed">
                Nous utilisons les services tiers suivants qui peuvent déposer des cookies :
              </p>
              <ul className="list-disc list-inside text-muted-foreground mt-4 space-y-2">
                <li><strong>Stripe</strong> : Traitement des paiements (cookies de sécurité)</li>
                <li><strong>Google</strong> : Authentification OAuth (si utilisée)</li>
              </ul>
              <p className="text-muted-foreground mt-4">
                Ces services ont leurs propres politiques de cookies que nous vous invitons à consulter.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">6. Comment supprimer les cookies</h2>
              <p className="text-muted-foreground leading-relaxed">
                Vous pouvez supprimer les cookies via votre navigateur :
              </p>
              <ul className="list-disc list-inside text-muted-foreground mt-4 space-y-2">
                <li><strong>Chrome :</strong> Paramètres → Confidentialité → Effacer les données</li>
                <li><strong>Firefox :</strong> Options → Vie privée → Cookies</li>
                <li><strong>Safari :</strong> Préférences → Confidentialité → Gérer les données</li>
                <li><strong>Edge :</strong> Paramètres → Confidentialité → Effacer les données</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">7. Contact</h2>
              <p className="text-muted-foreground leading-relaxed">
                Pour toute question sur notre politique de cookies : contact@simblyo.com
              </p>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
